import { Fragment, useState, useMemo, useRef } from 'react';
import { grabIdsFromData, outputData, saveData, formatJsonForBackend } from './utils/util';
import { useStyles } from './utils/appStyles';
import LoadData from './components/LoadData';
import AppHeader from './components/AppHeader';
import DeviceLevelForm from './components/DeviceLevelForm';
import DeviceDataView from './components/DeviceDataView';
import SelectDialog from './components/SelectDialog';
import schema from './schema.json';
import uischema from './uischema.json';

// import logo from './logo.svg';
import './App.css';

/**
 * Other Imports
 */
import { JsonForms } from '@jsonforms/react';
import { createAjv } from '@jsonforms/core';
import Notiflix from 'notiflix';

/**
 * Materia UI Imports
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  materialCells,
  materialRenderers,
} from '@jsonforms/material-renderers';
import HtmlTooltip from './components/HTMLTooltip';

const renderers = [
  ...materialRenderers,
  // register custom renderers
];

const App = () => {
  const [data, setData] = useState<any>();
  const [deviceData, setDeviceData] = useState<any>();
  const [dataObjects, setDataObjects] = useState<
    Array<{ elmentId: string; attributeId: string; deviceId: string }>
  >([]);
  const [attributes, setAttributes] = useState<string[]>(['']);
  const [clickable, setClickable] = useState(true);
  const [isDeviceView, setIsDeviceView] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);

  const stringifiedData = useMemo(() => {
    const formatJSON = outputData(data);
    return JSON.stringify(formatJSON, null, 2);
  }, [data]);

  const classes = useStyles();
  const handleDefaultsAjv = createAjv({ useDefaults: true });
  const initialRef: any = null;
  const childRef = useRef(initialRef);

  /**
   * This function shows a dialog using notiflix where the user has to enter an API key and then a button where the user can press "Authenticate".
   * If this works we immediately grab all projects for the user (the names and ids of the projects) and store it in the state. Also we store
   * all copmanies and their names first in the stae. For both we have the two calls:
   * https://backend.dataskop.at/api/company/list
   * https://backend.dataskop.at/api/projects/{id}
   * After both is done we show "LockOpen" icon from Material UI at the top right instead of the "Login" icon. Also if the icon is pressed again and we 
   * are logged in it should automatically just log out the user so we can add another API token.
   * 
   */
   const authenticateUser = () => {
    if (authenticated) {
      setApiKey('');
      setAuthenticated(false);
      return;
    }

    Notiflix.Confirm.prompt(
      'API Key Required',
      'Please enter your API key to authenticate:',
      '',
      'Authenticate',
      'Cancel',
      async (key) => {
        try {
          const companyResponse = await fetch('https://backend.dataskop.at/api/company/list', {
            headers: {
              'Authorization': `Bearer ${key}`
            }
          });
  
          if (!companyResponse.ok) {
            throw new Error('Failed to fetch companies');
          }
  
          const comp = await companyResponse.json();
          setApiKey(key);
          setCompanies(comp);
          setAuthenticated(true);
          Notiflix.Notify.success('Authentication successful!');
        } catch (error) {
          Notiflix.Notify.failure((error as any).message);
        }
      },
      () => {
        Notiflix.Notify.failure('Authentication cancelled.');
      },
      {
        width: '500px',
        plainText: false,
      }
    );
  };
  
  const fetchProjects = async (companyId: string) => {
    try {
      const projectResponse = await fetch(`https://backend.dataskop.at/api/project/list/prop/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });

      if (!projectResponse.ok) {
        throw new Error('Failed to fetch projects');
      }

      const projects = await projectResponse.json();
      setProjects(projects);
    } catch (error) {
      Notiflix.Notify.failure((error as any).message);
    }
  };

  const handleProjectSelect = async (projectId: string) => {
    const requestBody = formatJsonForBackend(data);

    console.log('Request Body:', requestBody);
    try {
      const response = await fetch(`https://backend.dataskop.at/api/project/update/${projectId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: requestBody
      });
  
      if (!response.ok) {
        const errorResponse = await response.json();
        console.error('Error response:', errorResponse);
        throw new Error(`Failed to post data: ${errorResponse.message || response.statusText}`);
      } else {
        console.log(response)
      }
  
      Notiflix.Notify.success('Data posted successfully!');
    } catch (error) {
      Notiflix.Notify.failure((error as any).message);
    }
  };
   
  /**
   * This function copies the current data to the clipboard.
   */
  const copyData = () => {
    const requestBody = formatJsonForBackend(data);
    navigator.clipboard.writeText(requestBody);
    Notiflix.Notify.success('Copied current data to clipboard!', {
      fontSize: '14px',
    });
  };

  /**
   * This function posts the data to the backend according to the corresponding ID from the current project.
   * The function first opens a dialog (notiflix) where the user has to add the authentification token if they are not authenticated.
   * If they are authenticated it should provide a dialog with a dropdown of the available projects for the user where theyx
   * can select the one they want to post it it. Then the post happens with this call: https://backend.dataskop.at/api/project/update/{id} 
   * with the current JSON result view that is stored in "data".
   */
   const postData = () => {
    if (!authenticated || !apiKey) {
      authenticateUser();
      return;
    }
  
    const fetchProjects = async (companyId: string) => {
      try {
        const projectResponse = await fetch(`https://backend.dataskop.at/api/project/list/prop/${companyId}`, {
          headers: {
            'Authorization': `Bearer ${apiKey}`
          }
        });
  
        if (!projectResponse.ok) {
          throw new Error('Failed to fetch projects');
        }
  
        const projects = await projectResponse.json();
        setProjects(projects);
      } catch (error) {
        Notiflix.Notify.failure((error as any).message);
      }
    };
  
    const handleProjectSelect = async (projectId: string) => {
      try {
        const response = await fetch(`https://backend.dataskop.at/api/project/update/${projectId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          },
          body: formatJsonForBackend(data)
        });
  
        if (!response.ok) {
          throw new Error('Failed to post data');
        }
  
        Notiflix.Notify.success('Data posted successfully!');
      } catch (error) {
        Notiflix.Notify.failure((error as any).message);
      }
    };
  
    // Show the custom dialog for selecting the company and project
    setDialogVisible(true);
  };
  
 
  /**
   * This function clears the whole data in the current session.
   */
  const clearData = () => {
    Notiflix.Confirm.show(
      'Clear Data',
      `You are about to clear the data you are working on currently. 
      <br/>Are you sure you want to do this? 
      <br/><br/> If yes click on <strong>"Proceed"</strong> otherwise <strong>"Cancel"</strong>.`,
      'Proceed',
      'Cancel',
      () => {
        setData({});
      },
      () => {
        return;
      },
      {
        width: '500px',
        messageMaxLength: 1000,
        plainText: false,
      }
    );
  };

  /**
   * This function handles the data save. If we press to often it shows a warning.
   */
  const handleDataSave = () => {
    if (clickable) {
      setClickable(false);
      saveData(data);
      childRef?.current?.updateD();

      setTimeout(() => {
        setClickable(true);
      }, 1000);
    } else {
      Notiflix.Notify.warning(`Too many clicks!! Saving still in progress!!`, {
        fontSize: '14px',
      });
    }
  };

  /**
   * Updates the current view with the previous data if it is present
   * @param data The data to be updated with
   */
  const updateJsonView = (data: object) => {
    setData(data);
  };

  /**
   * This toggles wehter it is the device view or the normal view.
   * @param e The event
   */
  const handleToggle = (e: any) => {
    setIsDeviceView(e.target.checked);
  };

  /**
   * =================================================
   *           BELOW -- Device Data Things
   * =================================================
   */

  /**
   * This is used in order to update the data from the device form and show it.
   */
  const handleDeviceDataChange = ({ errors, data }: any) => {
    setDeviceData(data);
  };

  /**
   * This fucntion removes only one indiviual device data. The one that is clicked on to be deleted.
   * @param id The device data to remove
   */
  const removeDeviceData = (id: string) => {
    setDataObjects((prevData: any) =>
      prevData.filter((data: any) => data.id !== id)
    );
  };

  /**
   * This function clears the whole device data in the current session.
   */
  const clearDeviceData = () => {
    Notiflix.Confirm.show(
      'Clear Device Data',
      `You are about to clear <strong>all</strong> the device data fields you are working on currently. 
      <br/>Are you sure you want to do this? 
      <br/><br/> If yes click on <strong>"Proceed"</strong> otherwise <strong>"Cancel"</strong>.`,
      'Proceed',
      'Cancel',
      () => {
        setDataObjects([]);
      },
      () => {
        return;
      },
      {
        width: '500px',
        messageMaxLength: 1000,
        plainText: false,
      }
    );
  };

  return (
    <Fragment>
      <AppHeader handleToggle={handleToggle} authenticateUser={authenticateUser} authenticated={authenticated}></AppHeader>
      <Grid
        container
        justifyContent={'center'}
        spacing={1}
        className={classes.container}
      >
        {!isDeviceView ? (
          <Fragment>
            <Grid item sm={6}>
              <Typography variant={'h4'} className={classes.title}>
                Form - Project Level
                <HtmlTooltip
                  title={
                    <Fragment>
                      <Typography color='inherit'>Form Explanation</Typography>
                      <hr/>
                      <p>In this form you are able to create configurations for the backend. Create a new attribute by clicking on 
                        the "+". This will create a sub form you can use to further configure the just created attribute. You can even provide
                        multiple visualization configurations for an attribute. The result of the configuration is immediately available on the right side as "JSON". 
                        You can copy the json then if you are done and put it into the backend.
                      </p>                      
                      <br/>
                      <p>For further help please refer to the official guide: <a href='https://google.at'>Guide</a></p>
                    </Fragment>
                  }
                >
                  <InfoTwoToneIcon
                    fontSize='medium'
                    style={{ marginLeft: '8px' }}
                  />
                </HtmlTooltip>
              </Typography>

              <Divider variant='middle' />
              <div className={classes.demoform}>
                <JsonForms
                  schema={schema}
                  uischema={uischema}
                  data={data}
                  renderers={renderers}
                  cells={materialCells}
                  onChange={({ errors, data }: any) => {
                    setAttributes(grabIdsFromData(data));
                    setData(data);
                  }}
                  validationMode={'ValidateAndShow'}
                  ajv={handleDefaultsAjv}
                />
              </div>
            </Grid>
            <Grid item sm={6}>
              <Typography variant={'h4'} className={classes.title}>
                JSON Result
              </Typography>
              <Divider variant='middle' />
              <div className='resultMenuBar'>
                <span className='menuBarLabel'>Controls: </span>
                <Tooltip title='Save current configuration' arrow>
                  <IconButton
                    onClick={() => handleDataSave()}
                    aria-label='save'
                    color='inherit'
                  >
                    <SaveIcon fontSize='large' />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Load a previous configuration' arrow>
                  <IconButton
                    onClick={() => childRef?.current?.oD()}
                    aria-label='load'
                    color='inherit'
                  >
                    <HourglassTopIcon fontSize='large' />
                  </IconButton>
                </Tooltip>
              </div>
              <div className={classes.dataContent}>
                <pre id='boundData'>{stringifiedData}</pre>
              </div>
              <Grid
                container
                justifyContent={'start'}
                alignItems={'center'}
                className={classes.container}
                spacing={1}
              >
                <Grid item>
                  <Button
                    className={classes.copyButton}
                    onClick={copyData}
                    color='primary'
                    variant='contained'
                    size='large'
                  >
                    <ContentCopyIcon sx={{ marginRight: '8px' }} />
                    Copy
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    className={classes.postButton}
                    onClick={postData}
                    color='primary'
                    variant='contained'
                    size='large'
                  >
                    <CloudUploadIcon sx={{ marginRight: '8px' }} />
                    Save to Database
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    className={classes.resetButton}
                    onClick={clearData}
                    color='primary'
                    variant='contained'
                    size='small'
                  >
                    <DeleteIcon sx={{ marginRight: '8px' }} />
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Fragment>
        ) : (
          <Fragment>
            <Grid item sm={6}>
              <Typography variant={'h4'} className={classes.title}>
                Form - Device Level
                <HtmlTooltip
                  title={
                    <Fragment>
                      <Typography color='inherit'>Form Explanation</Typography>
                      <hr/>
                      <p>In this form you can create the JSON objects for each device. Put them to the additional porperites field in the backend for each device.
                        You can select from the dropdown all attributeIds you have created previously on the other view. Also enter the deviceId you have given your device.
                        The objects are placed on the right and will not be overwritten. Whenever you create a new one it is added on top of the list.
                      </p>                      
                      <br/>
                      <p>For further help please refer to the official guide: <a href='https://google.at'>Guide</a></p>
                    </Fragment>
                  }
                >
                  <InfoTwoToneIcon
                    fontSize='medium'
                    style={{ marginLeft: '8px' }}
                  />
                </HtmlTooltip>
              </Typography>
              <Divider variant='middle' />
              <div className={classes.deviceForm}>
                <DeviceLevelForm
                  attributeIds={attributes}
                  deviceData={deviceData}
                  handleDeviceDataChange={handleDeviceDataChange}
                ></DeviceLevelForm>
                <Button
                  className={classes.createButton}
                  onClick={() => {
                    setDataObjects((prevDataObjects) => [
                      ...prevDataObjects,
                      { ...deviceData, id: `object-viewer-${Date.now()}` },
                    ]);
                  }}
                  color='primary'
                  variant='contained'
                  size='medium'
                >
                  <ContentCopyIcon style={{ marginRight: '8px' }} />
                  Create JSON
                </Button>
              </div>
            </Grid>
            <Grid item sm={6}>
              <Typography variant={'h4'} className={classes.title}>
                JSON Result
              </Typography>
              <Divider variant='middle' />
              <div className='resultMenuBar'>
                <span className='menuBarLabel'>Controls: </span>
                <Tooltip title='Clear all JSON results' arrow>
                  <IconButton
                    onClick={clearDeviceData}
                    aria-label='clear all'
                    color='inherit'
                  >
                    <DeleteIcon fontSize='large' />
                  </IconButton>
                </Tooltip>
              </div>
              <div className={classes.deviceDataContent}>
                {dataObjects.map((data: any, index: number) => (
                  <DeviceDataView
                    key={index}
                    data={data}
                    removeDeviceData={removeDeviceData}
                    authenticated={authenticated}
                    authenticateUser={authenticateUser}
                    fetchProjects={fetchProjects}
                    companies={companies}
                    projects={projects}
                    apiKey={apiKey}
                  />
                ))}
              </div>
            </Grid>
          </Fragment>
        )}
      </Grid>

      <LoadData anchor='right' updateJsonView={updateJsonView} ref={childRef} />

      {dialogVisible && (
        <SelectDialog
          companies={companies}
          fetchProjects={fetchProjects}
          projects={projects}
          onSelect={handleProjectSelect}
          onClose={() => setDialogVisible(false)}
        />
      )}
    </Fragment>
  );
};

export default App;
